import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import sitesHelper from "../../../../API/Sites/sitesHelper";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import BenchmarkSettingsView from "../SubComponents/BenchmarkSettingsView";
import benchmarksHelper from "../../../../API/Benchmarks/benchmarksHelper";
import { createBenchmark } from "../../../../graphql/mutations";

const BenchmarkSettings = forwardRef(
  (
    {
      sectionTitle,
      initialMapValues,
      settingType,
      isEdit,
      opt,
      setDisplayedView,
      backTo,
      onFunctionCreated,
      selectedId,
      setShowSiteModal,
      displayTokenSection,
      setDisplayTokenSection,
      setInitialMapValues,
      setShowBenchmarkModal,
    },
    ref
  ) => {
    const AppToasty = AppToast.useToast();
    const { t } = useTranslation();
    const { state, dispatch } = AppStateManager.useAppStateManager();
    const { getActiveWorkspace, getPlanConfig } = useWorkspaces();

    const validationSchema = AppForms.yup.object().shape({
      // allow for no more than 4 words in sourceLanguage and targetLanguage
      name: AppForms.yup.string().required().label().max(255),
      description: AppForms.yup.string().nullable().label().max(2048),
      questions: AppForms.yup.array().of(
        AppForms.yup.object().shape({
          question: AppForms.yup.string().nullable().label().max(255),
          answer: AppForms.yup.string().nullable().label().max(255),
        })
      ),
    });

    const handleDeletion = async (id) => {
      try {
        const result = await benchmarksHelper.deleteBenchmark(id);
        dispatch("SET_BENCHMARKS", [
          ...state.benchmarks?.filter((u) => u?.id !== id),
        ]);
        AppToasty.show(`Benchmark deleted successfully`, {
          type: "success",
        });

        setShowSiteModal(false);
      } catch (error) {
        console.error("benchmarkDeleteErrorCaption", error);
        AppToasty.show(`Benchmark deletion failed`, {
          type: "danger",
        });
      }
    };
    const identifyOptByID = (id) => {
      return state.benchmarks.find((u) => u?.id === id);
    };

    const handleBenchmarkItemSave = async (v) => {
      if (!v?.benchmarks || v.benchmarks?.length === 0)
        return AppToasty.show(
          `Please ensure the benchmark includes at least one question and answer`,
          {
            type: "danger",
          }
        );
      console.log("values", v);

      let newBenchmarks =
        state.benchmarks?.length > 0 ? [...state.benchmarks] : [];
      try {
        let result;
        if (isEdit) {
          if (v?.benchmarks) delete v.benchmarks;

          result = await benchmarksHelper.updateBenchmark(selectedId, [
            {
              property: "name",
              value: v.name,
            },
            {
              property: "description",
              value: v.description,
            },
            {
              property: "questions",
              value: v.benchmarks,
            },
            {
              property: "files",
              value: v.files,
            },
          ]);
          result = result?.data?.updateBenchmark;
          const _updatedObject = identifyOptByID(result?.id);
          const updateIndex = newBenchmarks.findIndex(
            (u) => u?.id === _updatedObject?.id
          );
          newBenchmarks[updateIndex] = result;
        } else {
          if (getPlanConfig()?.maxBenchmarks <= newBenchmarks.length)
            return AppToasty.show(
              `Your subscription plan does not allow you to add more than ${
                getPlanConfig()?.maxBenchmarks
              } benchmarks`,
              {
                type: "danger",
              }
            );
          v["questions"] = v?.benchmarks || [];
          delete v.benchmarks;
          result = await benchmarksHelper.createBenchmark({
            ...v,
            workspaceID: getActiveWorkspace()?.id,
          });
          result = result?.data?.createBenchmark;
          newBenchmarks.push(result);
        }

        dispatch("SET_BENCHMARKS", [...newBenchmarks]);
        if (!isEdit) {
          setInitialMapValues({
            ...result,
          });
        }
        setShowBenchmarkModal(false);
        backTo && navigateBack();
        onFunctionCreated && onFunctionCreated("success");
      } catch (error) {
        console.error(error);
        onFunctionCreated && onFunctionCreated("error");
      } finally {
        setShowBenchmarkModal(false);
      }
    };

    const _view = useMemo(
      () => (
        <AppFadeView
          style={{
            horizontalMargin: 10,
            paddingHorizontal: 20,
            minWidth: isLargeScreen() ? "100%" : 300,
            paddingBottom: 10,
            // height: 500,
          }}
        >
          {backTo && (
            <AppMenu.DefaultBackOptionComponent
              navigateBack={navigateBack}
              sectionTitle={"Benchmark : " + sectionTitle}
              isClickable
              isBackIconDisplayed
            />
          )}

          <AppForms.AppForm
            ref={ref}
            initialValues={initialMapValues}
            onSubmit={handleBenchmarkItemSave}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <BenchmarkSettingsView
              setDisplayTokenSection={setDisplayTokenSection}
              displayTokenSection={displayTokenSection}
              handleDeletion={handleDeletion}
              isEdit={isEdit}
              settingType={settingType}
            />
          </AppForms.AppForm>
        </AppFadeView>
      ),
      [
        state.activeWorkspaceID,
        JSON.stringify(initialMapValues),
        JSON.stringify(state.benchmarks),
        isEdit,
        displayTokenSection,
        AppToasty,
      ]
    );
    return _view;
  }
);

export default BenchmarkSettings;
